import { ChangeEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Box, SelectChangeEvent, Toolbar } from '@mui/material';
import clsx from 'clsx';
import * as R from 'ramda';

import { requestTenantCreation } from '@atom/actions/organizationActions';
import { triggerLogout } from '@atom/actions/userActions';
import Logo from '@atom/components/common/navigation/Logo';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Button, Checkbox, Icon, Progress, Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import layoutStyles from '@atom/styles/layout';
import { RootState } from '@atom/types/store';
import accountUtilities from '@atom/utilities/accountUtilities';

import LoginFooter from './LoginFooter';

import classes from './login.module.css';

const { MenuItem } = Select;

const navBarStyle = {
  height: layoutStyles.topNavHeight,
  backgroundColor: colors.neutral.white,
  boxShadow: '0 2px 4px 1px',
  color: colors.utility.boxShadow,
  position: 'fixed',
  top: 0,
};

const styles = {
  titleStyle: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
  },
  activeIcon: {
    fontSize: '24px',
    color: colors.neutral.white,
  },
};

interface ErrorsState {
  tenant: boolean;
  terms: boolean;
  industry: boolean;
}

const INDUSTRIES = [
  'Preventative Maintenance',
  'Mobile Workforce',
  'Transportation | Traffic',
  'Utilities',
  'Fleet Management',
  'Emergency Response',
  'Equipment Management',
  'Rail | Transit',
  'Stormwater Management',
  'Facility Management',
  'Other',
];

type Industries = (typeof INDUSTRIES)[number] | '';

const DEV_TENANT_NAME_VALIDATION = new RegExp('^dev\\-[-a-zA-Z]+$');
const QA_TENANT_NAME_VALIDATION = new RegExp('^qa\\-[-a-zA-Z]+$');
const UAT_TENANT_NAME_VALIDATION = new RegExp('^uat\\-[-a-zA-Z]+$');
const DEFAULT_TENANT_NAME_VALIDATION = new RegExp('^[a-zA-Z][-a-zA-Z]+$');

const TenantCreation = () => {
  const [, { Link, navigate }] = useNavigation();
  const [tenant, setTenant] = useState<string>('');
  const [industry, setIndustry] = useState<Industries>('');
  const [termsCheck, setTermsCheck] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorsState>({
    tenant: false,
    terms: false,
    industry: false,
  });

  const email = accountUtilities.getLoggedInEmail();

  if (!email) {
    navigate('/login');
  }

  if (!accountUtilities.isEmailVerified()) {
    navigate('/verify-email');
  }

  const dispatch = useDispatch();
  const loading = useSelector(
    (state: RootState) => state.loading.loadingTenantCreation,
  );

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTenant(value);
  };

  const onIndustryChange = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setIndustry(value);
  };

  const onToggleTermsCheck = () => {
    setTermsCheck(prevTermsCheck => !prevTermsCheck);
  };

  const getCurrentHost = (): string => {
    return window.location.hostname.split('.')[2]
      ? R.pipe(
          R.pathOr('www.atomapp.com', ['location', 'hostname']),
          R.split('.'),
          R.drop(1),
          R.join('.'),
        )(window)
      : R.pipe(R.pathOr('www.atomapp.com', ['location', 'hostname']))(window);
  };

  const isValidTenantName = (host: string): boolean => {
    if (host === 'dev-atom' || host === 'local-atom') {
      return DEV_TENANT_NAME_VALIDATION.test(tenant);
    }

    if (host === 'qa-atom') {
      return QA_TENANT_NAME_VALIDATION.test(tenant);
    }

    if (host === 'uat-atom') {
      return UAT_TENANT_NAME_VALIDATION.test(tenant);
    }

    if (
      host === 'atomapp' &&
      (DEV_TENANT_NAME_VALIDATION.test(tenant) ||
        QA_TENANT_NAME_VALIDATION.test(tenant) ||
        UAT_TENANT_NAME_VALIDATION.test(tenant))
    ) {
      return false;
    } else if (host === 'atomapp') {
      return DEFAULT_TENANT_NAME_VALIDATION.test(tenant);
    }

    return false;
  };

  const isValid = (): boolean => {
    const host = window.location.hostname.split('.')[2]
      ? window.location.hostname.split('.')[1]
      : window.location.hostname.split('.')[0];

    setErrors(prevErrors => ({
      ...prevErrors,
      tenant: !isValidTenantName(host),
      terms: !termsCheck,
      industry: industry === '',
    }));

    return isValidTenantName(host) && termsCheck && industry !== '';
  };

  const submit = () => {
    if (isValid()) {
      dispatch(
        requestTenantCreation({
          name: tenant.toLocaleLowerCase(),
          industry,
        }),
      );
    }
  };

  const getErrorText = () => {
    if (errors.tenant) {
      return (
        <div className={classes.tenantCreationErrorText}>
          Please enter a valid tenant name. The name cannot include any numbers
          or special characters.
        </div>
      );
    }

    if (errors.terms) {
      return (
        <div className={classes.tenantCreationErrorText}>
          Please agree to the terms before continuing.
        </div>
      );
    }

    if (errors.industry) {
      return (
        <div className={classes.tenantCreationErrorText}>
          Please select an industry.
        </div>
      );
    }

    return <div />;
  };

  const logout = () => {
    dispatch(triggerLogout());
  };

  const content = loading ? (
    <div className={classes.spinnerContainer}>
      <Progress />
    </div>
  ) : (
    <>
      {getErrorText()}
      <div className={clsx(classes.textFieldContainer, classes.creation)}>
        This is going to be your workspace URL.
      </div>
      <div className={classes.tenantCreationInputContainer}>
        <TextField
          variant="standard"
          name="tenant"
          onChange={onChange}
          value={tenant}
        />
        <div
          className={classes.tenantCreationInputLabel}
        >{`.${getCurrentHost()}`}</div>
      </div>
      <div className={classes.tenantCreationIndustryContainer}>
        <Select
          fullWidth
          label="Select Your Industry"
          onChange={onIndustryChange}
          value={industry}
        >
          {INDUSTRIES.map((option: string) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className={classes.tenantCreationCheckboxContainer}>
        <Checkbox checked={termsCheck} onChange={onToggleTermsCheck} />
        <div className={classes.tenantCreationTermsContent}>
          I agree to the Terms of Use and Privacy Policy.
        </div>
      </div>
      <div className={classes.tenantCreationButtonContainer}>
        <Button color="primary" variant="contained" onClick={submit}>
          Go to Atom
        </Button>
      </div>
      <div className={classes.workspaceSelectionContainer}>
        <div>Already have a workspace?</div>
        <Link to="/tenant-entry">
          <div className={classes.tenantSelectionLink}>Join a workspace</div>
        </Link>
      </div>
    </>
  );

  return (
    <>
      <AppBar style={navBarStyle}>
        <Toolbar>
          <div>
            <Logo />
          </div>
          <Box sx={{ flexGrow: 1 }} />
          <div className={classes.navRightContainer}>
            <div className={classes.navRightText}>{email}</div>
            <div className={classes.navRightLogin} onClick={logout}>
              Sign Out
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <div className={classes.container}>
        <div className={clsx(classes.authBackground, classes.complete)} />
        <div className={classes.headingText}>Create your Atom environment</div>
        <div className={classes.registrationIconContainer}>
          <div className={clsx(classes.registrationIcon, classes.active)}>
            <Icon style={styles.activeIcon}>person</Icon>
          </div>
          <div className={clsx(classes.registrationIcon, classes.active)}>
            <Icon style={styles.activeIcon}>email</Icon>
          </div>
          <div className={clsx(classes.registrationIcon, classes.active)}>
            <Icon style={styles.activeIcon}>business</Icon>
          </div>
        </div>
        <div className={classes.iconHorizontalBarContainer}>
          <div
            className={clsx(classes.iconHorizontalBarHalf, classes.active)}
          />
          <div
            className={clsx(classes.iconHorizontalBarHalf, classes.active)}
          />
        </div>
        {content}
        <LoginFooter />
      </div>
    </>
  );
};

export default TenantCreation;
